import {
  FileFolderItem,
  isModelVisible,
  isPointCloudVisible,
  isTrajectoryVisible,
  FeatureExtractionLayersLoadStatus,
  MissionDetailsSelection,
  RunDetailsSelection,
  useDataContext,
  useFeatureExtractionContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { t } from '@trimble/i18n';
import { sortItems } from '@trimble/ui-components/solidjs';
import { Empty } from '@tcweb/connect-ui';
import { JSX, Show, createMemo } from 'solid-js';
import { useProjectDataPanel } from '@trimble/project-data-panel-ui';
import { SharedTrajectoryTreeContainer } from './SharedTrajectoryTreeContainer';
import { SharedPointCloudsTreeContainer } from './SharedPointCloudsTreeContainer';
import { SharedModelsTreeContainer } from './SharedModelsTreeContainer';
import { SharedMissionTreeContainer } from './SharedMissionTreeContainer';
import { SharedWorkspacesTreeContainer } from './SharedWorkspacesTreeContainer';
import {
  buildMissionNodes,
  buildModelNodes,
  buildPointCloudNodes,
  buildTrajectoryNodes,
  MissionTreeNodeType,
  MODEL_ROOT_NODE_ID,
  POINT_CLOUD_ROOT_NODE_ID,
  TRAJECTORY_ROOT_NODE_ID,
} from '../project-data-tree-nodes';
import {
  buildWorkspaceNodes,
  getLayerIdFromNodeId,
} from '../workspace-tree-nodes';
import {
  countRootNodes,
  filterProjectDataNodes,
  getNodesForSelectionTree,
} from '../project-data-filtering';
import { UnreachableCaseError } from 'ts-essentials';

type ChildrenLoadStatus = 'pending' | 'success' | 'error';

const getChildrenLoadStatus = (state: LoadState): ChildrenLoadStatus =>
  state === LoadState.LoadComplete ? 'success' : 'pending';

const getWorkspacesChildrenLoadStatus = (
  status: FeatureExtractionLayersLoadStatus
): ChildrenLoadStatus => {
  switch (status) {
    case 'idle':
    case 'pending':
      return 'pending';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    default:
      throw new UnreachableCaseError(status);
  }
};

export interface SharedProjectDataTreesProps {
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
}

export const SharedProjectDataTrees = (
  props: SharedProjectDataTreesProps
): JSX.Element => {
  const { dataState } = useDataContext();
  const featureExtraction = useFeatureExtractionContext();
  const { filterText, filterMode } = useProjectDataPanel();

  const layersLoadStatus = createMemo(() =>
    featureExtraction.layersLoadStatus()
  );

  const trajectoryNodes = createMemo(() =>
    dataState.trajectory.state === LoadState.LoadComplete
      ? buildTrajectoryNodes(Object.values(dataState.trajectory.items))
      : []
  );

  const pointCloudNodes = createMemo(() =>
    dataState.pointCloud.state === LoadState.LoadComplete
      ? buildPointCloudNodes(Object.values(dataState.pointCloud.items))
      : []
  );

  const missionNodes = createMemo(() =>
    dataState.mission.state === LoadState.LoadComplete
      ? buildMissionNodes(Object.values(dataState.mission.items))
      : []
  );

  const modelNodes = createMemo(() =>
    dataState.tcFileFolder.state === LoadState.LoadComplete
      ? buildModelNodes(Object.values(dataState.tcFileFolder.items))
      : []
  );

  const filteredModelNodes = createMemo(() =>
    filterProjectDataNodes(
      modelNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== MODEL_ROOT_NODE_ID && isModelVisible(dataState, node.id)
    )
  );

  const showWorkspaces = createMemo(
    () =>
      featureExtraction.isFeatureExtractionEnabled() &&
      featureExtraction.scope()?.projectId !== undefined
  );

  const workspaceNodes = createMemo(() => {
    if (!showWorkspaces()) {
      return [];
    }

    const status = layersLoadStatus();
    return buildWorkspaceNodes(
      featureExtraction.editingState.workspaces,
      featureExtraction.editingState.layers,
      { includeRootWhenEmpty: status !== 'error' }
    );
  });

  const filteredWorkspaceNodes = createMemo(() =>
    filterProjectDataNodes(
      workspaceNodes(),
      filterMode(),
      filterText(),
      (node) => {
        const layerId = getLayerIdFromNodeId(node.id);
        return (
          layerId !== undefined && featureExtraction.isLayerVisible(layerId)
        );
      }
    )
  );

  const filteredTrajectoryNodes = createMemo(() =>
    filterProjectDataNodes(
      trajectoryNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== TRAJECTORY_ROOT_NODE_ID &&
        isTrajectoryVisible(dataState, node.id)
    )
  );

  const filteredPointCloudNodes = createMemo(() =>
    filterProjectDataNodes(
      pointCloudNodes(),
      filterMode(),
      filterText(),
      (node) =>
        node.id !== POINT_CLOUD_ROOT_NODE_ID &&
        isPointCloudVisible(dataState, node.id)
    )
  );

  const filteredMissionNodes = createMemo(() =>
    filterProjectDataNodes(
      missionNodes(),
      filterMode(),
      filterText(),
      (node) => {
        const type = node.data?.type;
        switch (type) {
          case MissionTreeNodeType.Run:
            return isPointCloudVisible(dataState, node.id);
          case MissionTreeNodeType.Path:
            return (
              isTrajectoryVisible(dataState, node.id) ||
              (node.data?.mission?.runs.some((run) =>
                isPointCloudVisible(dataState, run.id)
              ) ??
                false)
            );
          case MissionTreeNodeType.Mission:
          case MissionTreeNodeType.Runs:
          case undefined:
            return false;
          default:
            throw new UnreachableCaseError(type);
        }
      }
    )
  );

  const isModelsSectionEmpty = createMemo(() => {
    if (dataState.tcFileFolder.state !== LoadState.LoadComplete) {
      return true;
    }

    return countRootNodes(filteredModelNodes().nodes) === 0;
  });

  const areAllFilteredTreesEmpty = createMemo(() => {
    const workspacesEmpty =
      !showWorkspaces() || countRootNodes(filteredWorkspaceNodes().nodes) === 0;

    return (
      countRootNodes(filteredTrajectoryNodes().nodes) === 0 &&
      countRootNodes(filteredPointCloudNodes().nodes) === 0 &&
      countRootNodes(filteredMissionNodes().nodes) === 0 &&
      workspacesEmpty &&
      isModelsSectionEmpty()
    );
  });

  const hasFiltersApplied = createMemo(() => {
    return filterMode() !== 'all' || filterText().trim().length > 0;
  });

  const trajectoryControl = (
    <SharedTrajectoryTreeContainer
      nodes={getNodesForSelectionTree(trajectoryNodes(), filterText())}
      childrenLoadStatus={getChildrenLoadStatus(dataState.trajectory.state)}
    />
  );

  const pointCloudControl = (
    <SharedPointCloudsTreeContainer
      nodes={getNodesForSelectionTree(pointCloudNodes(), filterText())}
      childrenLoadStatus={getChildrenLoadStatus(dataState.pointCloud.state)}
    />
  );

  const modelControl = (
    <SharedModelsTreeContainer
      onModelDetailsSelect={props.onModelDetailsSelect}
    />
  );
  const missionControl = (
    <SharedMissionTreeContainer
      nodes={getNodesForSelectionTree(missionNodes(), filterText())}
      childrenLoadStatus={getChildrenLoadStatus(dataState.mission.state)}
      onRunDetailsSelect={props.onRunDetailsSelect}
      onMissionDetailsSelect={props.onMissionDetailsSelect}
    />
  );

  const workspacesControl = (
    <SharedWorkspacesTreeContainer
      nodes={getNodesForSelectionTree(workspaceNodes(), filterText())}
      childrenLoadStatus={getWorkspacesChildrenLoadStatus(layersLoadStatus())}
    />
  );

  interface ControlMap {
    control: JSX.Element;
    label: string;
    order: number;
  }

  const treeControls = createMemo((): ControlMap[] => {
    const controls: ControlMap[] = [
      {
        control: trajectoryControl,
        label: t('mobileMapping.projectData.trajectories'),
        order: 5,
      },
      {
        control: pointCloudControl,
        label: t('mobileMapping.projectData.pointClouds'),
        order: 3,
      },
      {
        control: modelControl,
        label: t('mobileMapping.projectData.models'),
        order: 4,
      },
      {
        control: missionControl,
        label: t('mobileMapping.missions.missions'),
        order: 2,
      },
    ];

    if (showWorkspaces()) {
      controls.push({
        control: workspacesControl,
        label: t('mobileMapping.workspace.title'),
        order: 1,
      });
    }

    return controls;
  });

  const sortedTreeControls = createMemo(() =>
    [...treeControls()]
      .sort((a, b) => sortItems(a, b, 'order', 'asc'))
      .map(({ control }) => control)
  );

  return (
    <Show
      when={!areAllFilteredTreesEmpty()}
      fallback={
        <Empty
          icon="tc-icon-folder"
          title={t('mobileMapping.projectData.noData.title')}
          message={
            hasFiltersApplied()
              ? t('mobileMapping.projectData.noData.messageWithFilters')
              : t('mobileMapping.projectData.noData.message')
          }
          class="flex-1 w-100"
          data-testid="project-data-no-data-found"
          isSmallContainer={true}
        />
      }
    >
      {sortedTreeControls()}
    </Show>
  );
};
